import { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '@/services/admin.service';
import { useToast } from '@/contexts/ToastContext';
import type { AdminBadge, BadgeCriteria } from '@/types/admin';

function describeCriteria(criteria: BadgeCriteria): string {
  switch (criteria.type) {
    case 'trails_completed':
      return `Completar ${criteria.count || 0} cursos`;
    case 'xp_earned':
      return `Acumular ${criteria.xp || 0} XP`;
    case 'trails_completed_with_tag':
      return `Completar ${criteria.count || 0} cursos com tag específica`;
    case 'level_reached':
      return `Atingir nível ${criteria.level || 0}`;
    case 'lessons_completed':
      return `Completar ${criteria.count || 0} lições`;
    case 'games_won':
      return `Vencer ${criteria.count || 0} jogos`;
    case 'missions_completed':
      return `Completar ${criteria.count || 0} missões`;
    case 'lessons_in_day':
      return `Completar ${criteria.count || 0} lições em um dia`;
    default:
      return criteria.type;
  }
}

const RARITY_COLORS: Record<string, string> = {
  COMMON: 'text-gray-400',
  RARE: 'text-blue-400',
  EPIC: 'text-purple-400',
  LEGENDARY: 'text-yellow-400',
};

const AdminBadgesPage: FC = () => {
  const { showToast } = useToast();
  const [badges, setBadges] = useState<AdminBadge[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadBadges();
  }, []);

  const loadBadges = async () => {
    try {
      const data = await adminService.getBadges();
      setBadges(data);
    } catch { /* ignore */ } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Excluir esta badge?')) return;
    try {
      await adminService.deleteBadge(id);
      await loadBadges();
    } catch {
      showToast('Erro ao excluir badge', 'error');
    }
  };

  return (
    <div className="p-6 lg:p-10">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <h1 className="text-2xl font-bold">Badges</h1>
        <Link
          to="/admin/badges/novo"
          className="bg-accent hover:bg-accent-hover text-accent-contrast px-5 py-2.5 rounded-xl font-medium transition-colors flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-xl">add</span>
          Nova Badge
        </Link>
      </div>

      {isLoading ? (
        <p className="text-content-muted">Carregando...</p>
      ) : badges.length === 0 ? (
        <p className="text-content-muted">Nenhuma badge criada.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-theme-border text-left text-content-muted text-sm">
                <th className="pb-3 font-medium">Badge</th>
                <th className="pb-3 font-medium">Critério</th>
                <th className="pb-3 font-medium text-center">Raridade</th>
                <th className="pb-3 font-medium text-center">XP</th>
                <th className="pb-3 font-medium text-center">Desbloqueadas</th>
                <th className="pb-3 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {badges.map((badge) => (
                <tr key={badge.id} className="border-b border-theme-border/50 hover:bg-surface-hover/50">
                  <td className="py-4">
                    <div className="flex items-center gap-3">
                      {badge.icon_url ? (
                        <img src={badge.icon_url} alt="" className="w-8 h-8 rounded" />
                      ) : (
                        <span className="material-symbols-outlined text-2xl text-accent">{badge.icon || 'military_tech'}</span>
                      )}
                      <div>
                        <p className="font-medium">{badge.title}</p>
                        <p className="text-content-muted text-sm truncate max-w-xs">{badge.description}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 text-sm text-content-secondary">{describeCriteria(badge.criteria)}</td>
                  <td className="py-4 text-center">
                    <span className={`text-sm font-medium ${RARITY_COLORS[badge.rarity] || ''}`}>
                      {badge.rarity}
                    </span>
                  </td>
                  <td className="py-4 text-center">{badge.xp_reward}</td>
                  <td className="py-4 text-center">{badge.total_unlocked}</td>
                  <td className="py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Link
                        to={`/admin/badges/${badge.id}/editar`}
                        className="p-2 rounded-lg hover:bg-surface-hover text-content-muted hover:text-content-primary transition-colors"
                        title="Editar"
                      >
                        <span className="material-symbols-outlined text-xl">edit</span>
                      </Link>
                      <button
                        onClick={() => handleDelete(badge.id)}
                        className="p-2 rounded-lg hover:bg-red-500/10 text-content-muted hover:text-red-400 transition-colors"
                        title="Excluir"
                      >
                        <span className="material-symbols-outlined text-xl">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminBadgesPage;
